import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Player } from '../types';
import { useDivision } from './DivisionContext';
import { getFirebasePrefix } from '../utils';

// Define the shape of the data the context will provide
interface PlayerContextType {
  players: Player[];
  draftablePlayers: Player[];
  substitutes: Player[];
  loading: boolean;
  getPlayerById: (id: number, isSub?: boolean) => Player | undefined;
}

// Create the context with a default value
const PlayerContext = createContext<PlayerContextType | undefined>(undefined);

// Create the Provider component
export const PlayerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [hash, setHash] = useState(window.location.hash);
  useEffect(() => {
    const handleHashChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const [players, setPlayers] = useState<Player[]>([]);
  const [draftablePlayers, setDraftablePlayers] = useState<Player[]>([]);
  const [substitutes, setSubstitutes] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);

  const { division } = useDivision();

  // This effect runs once when the provider mounts to fetch all player data
  useEffect(() => {
    const fetchPlayers = async () => {
      setLoading(true);
      try {
        const prefix = getFirebasePrefix();
        const playersRef = doc(db, 'players', `${prefix}_${division}`);
        const snapshot = await getDoc(playersRef);

        if (snapshot.exists()) {
            const data = snapshot.data();
            setPlayers(data.players || []);
            setSubstitutes(data.subs || []);
        } else {
            setPlayers([]);
            setSubstitutes([]);
        }
      } catch (error) {
        console.error("Failed to fetch player data:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchRemainingDraftablePlayers = async () => {
      setLoading(true);

      try {
        const prefix = getFirebasePrefix();
        const draftRef = doc(db, 'drafts', `${prefix}_${division}`);
        const snapshot = await getDoc(draftRef);

        if (snapshot.exists()) {
          setDraftablePlayers(snapshot.data().availablePlayers);
        } else {
          setDraftablePlayers([]);
        }
      } catch (error) {
        console.error("Failed to fetch draft data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchPlayers();
    fetchRemainingDraftablePlayers();
  }, [division, hash]);

  // Helper function to find a player by ID, memoized for performance
  const getPlayerById = (id: number, isSub?: boolean) => {
    if (isSub) {
      return substitutes.find(p => p.id === id);
    }
    return players.find(p => p.id === id);
  };

  const value = {
    players,
    draftablePlayers,
    substitutes,
    loading,
    getPlayerById,
  };

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
};

// Create a custom hook for easy access to the context
export const usePlayers = (): PlayerContextType => {
  const context = useContext(PlayerContext);
  if (context === undefined) {
    throw new Error('usePlayers must be used within a PlayerProvider');
  }
  return context;
};
